import { useState, useEffect, useCallback } from 'react'
import { Grid, Hidden } from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import { Card, CardContent } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Filters from './Filters'
import AppFilters from '../../../AppFilters'
import AddressesTable from './AddressesTable'
import PageTitle from '../../../Misc/PageTitle'
import { toast } from 'react-toastify'
import ButtonWrapper from '../../../FormUI/ButtonWrapper/ButtonWrapper'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Stack } from '@mui/material'
import MerchantMenu from '../MerchantMenu'
import MerchantAlias from '../MerchantAlias'
import MerchantSubmenu from '../MerchantSubmenu'
import useIsMounted from '../../../../hooks/useIsMounted'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'

import useHasPermissions from 'hooks/useHasPermissions'
import { ADDRESSES__CREATE } from '../../../../enums/Caps'


export default function AddressesList() {
    const hasPermissions = useHasPermissions()

    const { t } = useTranslation()

    const navigate = useNavigate()

    const isMounted = useIsMounted()

    const api = useAxiosPrivate()

    const { userId } = useParams()

    const [params, setParams] = useState({})

    const [loading, setLoading] = useState(true)

    const [data, setData] = useState([])

    const [meta, setMeta] = useState({
        total_pages: 1,
        current_page: 1
    })

    const handleOnSearch = values => {
        setParams(values)
    }

    const handleOnPageChange = (e, page) => {
        setMeta({ ...meta, current_page: page })
        setParams({ ...params, page: page })
    }

    const retrieveData = useCallback(() => {
        setData([])

        api.get(`/internal/merchants/${userId}/addresses/`, { params: params })
            .then(response => {
                if (isMounted()) {
                    const { meta, data } = response.data;

                    setMeta(meta)

                    setData(data)
                }
            })
            .catch(() => isMounted() && toast.error(t('Something went wrong')))
            .finally(() => isMounted() && setLoading(false))
    }, [params, userId, t, api, isMounted])

    const handleRemove = (id) => {
        return api.delete(`/internal/merchants/${userId}/addresses/${id}/`)
            .then(() => {
                if (isMounted()) {
                    toast.success(t('Address has been deleted'))
                    setData(data.filter(d => d.id !== id))
                }
            })
            .catch(() => isMounted() && toast.error(t('Something went wrong')))
    }

    useEffect(() => {
        const init = () => {
            setLoading(true)
            retrieveData()
        }

        init()
    }, [params, retrieveData])

    return (
        <>
            <Card>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <PageTitle title={t('Merchant addresses')} />

                        <Hidden xlUp={true}>
                            {userId ? <MerchantMenu id={userId} /> : ''}
                        </Hidden>
                    </Stack>

                    <MerchantAlias />

                    <Grid container spacing={2} mt={2} mb={2}>
                        <Grid item xs={6}>
                            {hasPermissions(ADDRESSES__CREATE) && (<ButtonWrapper
                                variant="contained"
                                component={Link}
                                to="create"
                                size="small"
                                startIcon={<AddIcon />}>{t('New')}</ButtonWrapper>)}
                        </Grid>

                        <Grid item xs={6} sx={{
                            textAlign: 'right'
                        }}>
                            <ButtonWrapper
                                variant="text"
                                size="small"
                                onClick={() => navigate('/merchants')}
                                startIcon={<ArrowBackIcon />}>{t('Go back')}</ButtonWrapper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} xl={2}>
                            <MerchantSubmenu current='addresses' id={userId} />
                        </Grid>

                        <Grid item xs={12} xl={10}>
                            <AppFilters Filters={<Filters handleOnSearch={handleOnSearch} />} />

                            <AddressesTable
                                data={data}
                                pages={meta.total_pages}
                                page={meta.current_page}
                                loading={loading}
                                handleOnPageChange={handleOnPageChange}
                                handleRemove={handleRemove}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </>
    )
}
